import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import TableCell from 'src/components/Table/TableCell';
import Table from 'src/components/Table';
import NotFoundRecords from 'src/components/NotFoundRecords';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { LOAD_CONTRACT_ACCOUNTING_CAPITAL_BALANCE } from 'src/store/actions/contractActions';
import AccountingCapitalBalanceRow from './AccountingCapitalBalanceRow';

const Tbody = ({ balanceList }) =>
  balanceList.map((item, index) => (
    <AccountingCapitalBalanceRow
      key={`${item.clientId}-${item.contractId || index}`}
      {...item}
    />
  ));

Tbody.propTypes = {
  balanceList: PropTypes.array.isRequired
};

const AccountingCapitalBalanceTable = ({ balanceList, ...rest }) => {
  const { t } = useTranslation();
  const loading = useSelector(
    isLoadingSelector([LOAD_CONTRACT_ACCOUNTING_CAPITAL_BALANCE.action])
  );

  const theads = useMemo(
    () => () => (
      <TableRow>
        <TableCell variant="head" align="center" sx={{ width: '10%' }}>
          {t('CONTRACT_VIEW.CONTRACT_DETAIL.PERIOD')}
        </TableCell>
        <TableCell variant="head" align="center" sx={{ width: '10%' }}>
          {t('COMMON.DATE')}
        </TableCell>
        <TableCell variant="head" align="center" sx={{ width: '10%' }}>
          {t('COMMON.INITIAL_BALANCE')}
        </TableCell>
        <TableCell variant="head" align="center" sx={{ width: '10%' }}>
          {t('COMMON.INVESTMENT')}
        </TableCell>
        <TableCell variant="head" align="center" sx={{ width: '10%' }}>
          {t('COMMON.INITIAL_PAYMENT')}
        </TableCell>
        <TableCell variant="head" align="center" sx={{ width: '10%' }}>
          {t('COMMON.MONTHLY_PRINCIPAL')}
        </TableCell>
        <TableCell variant="head" align="center" sx={{ width: '10%' }}>
          {t('COMMON.CONTRIBUTION')}
        </TableCell>
        <TableCell variant="head" align="center" sx={{ width: '10%' }}>
          {t('COMMON.RESIDUAL')}
        </TableCell>
        <TableCell variant="head" align="center" sx={{ width: '10%' }}>
          {t('CONTRACT_VIEW.EVENTS.SETTLEMENT.RESULT')}
        </TableCell>
        <TableCell variant="head" align="center" sx={{ width: '10%' }}>
          {t('COMMON.FINAL_BALANCE')}
        </TableCell>
      </TableRow>
    ),
    [t]
  );

  const tbody = useMemo(() => () => <Tbody balanceList={balanceList} />, [
    balanceList
  ]);

  if (!loading && !balanceList.length) {
    return (
      <Card>
        <NotFoundRecords
          title="NOT_FOUND_RECORDS_MESSAGE.BALANCE_CAPITAL_TITLE"
          description="NOT_FOUND_RECORDS_MESSAGE.BALANCE_CAPITAL_DESCRIPTION"
        />
      </Card>
    );
  }

  return (
    <Table
      variant="outlined"
      tbody={tbody}
      theads={theads}
      loading={loading}
      // Without a fixed layout the browser treats the header widths as mere
      // suggestions and sizes each column by its content, so the ones holding
      // long headers and six-figure amounts end up far wider than the rest.
      tableSx={{ tableLayout: 'fixed' }}
      {...rest}
    />
  );
};

AccountingCapitalBalanceTable.propTypes = {
  balanceList: PropTypes.array.isRequired
};

export default AccountingCapitalBalanceTable;
